import React from 'react';
import { Phone, Clock, Shield, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo, whyChooseUs } from '../data/mock';

const HeroSection = ({ onNavigate }) => {
  return (
    <section id="home" className="relative min-h-screen flex items-center pt-12">
      
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/6471911/pexels-photo-6471911.jpeg?auto=compress&cs=tinysrgb&w=1920')`
        }}
      />
      
      {/* Overlay */}
      <div className="absolute inset-0 hero-overlay" />
      
      {/* Subtle grain texture */}
      <div className="absolute inset-0 opacity-10">
        <div className="w-full h-full" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 pt-44">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 animate-fade-in">
              <Shield size={16} className="text-[#DA9C3A]" />
              <span className="text-sm font-medium">100% Satisfaction Guaranteed</span>
            </div>
            
            <h1 className="romano-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 animate-fade-in-up">
              AC Repair &amp; HVAC Service
              <span className="block text-[#DA9C3A]">Roseville CA</span>
              Since 2005
            </h1>
            
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-xl animate-fade-in-up delay-100">
              Expert AC repair, HVAC repair, furnace repair &amp; air conditioning service in Roseville CA. Serving Roseville, Rocklin, Folsom, El Dorado, South Placer County, Sacramento County &amp; San Joaquin County. Stay comfortable year-round.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-10 animate-fade-in-up delay-200">
              <a href={`tel:${companyInfo.phone}`}>
                <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-bold px-8 py-6 text-lg rounded-full shadow-xl hover:shadow-2xl transition-all w-full sm:w-auto">
                  <Phone size={20} className="mr-2" />
                  Get Free Estimate
                </Button>
              </a>
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#622B62] font-bold px-8 py-6 text-lg rounded-full transition-all w-full sm:w-auto"
                onClick={() => onNavigate('services')}
              >
                Our Services
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6 animate-fade-in-up delay-300">
              <div className="text-center">
                <p className="text-3xl font-bold text-[#DA9C3A]">20+</p>
                <p className="text-sm text-white/80">Years Experience</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-[#DA9C3A]">24/7</p>
                <p className="text-sm text-white/80">Emergency Service</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-[#DA9C3A]">5000+</p>
                <p className="text-sm text-white/80">Happy Customers</p>
              </div>
            </div>
          </div>

          {/* Right Content - Feature Cards */}
          <div className="hidden lg:block">
            <div className="space-y-4 animate-slide-in-right">
              {whyChooseUs.map((item, index) => (
                <div
                  key={item.id}
                  className="glass-card p-5 rounded-xl flex items-center gap-4 hover:scale-[1.02] transition-transform"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-12 h-12 rounded-full bg-[#622B62] flex items-center justify-center flex-shrink-0">
                    {index === 0 && <Clock size={22} className="text-[#DA9C3A]" />}
                    {index === 1 && <Shield size={22} className="text-[#DA9C3A]" />}
                    {index === 2 && (
                      <svg className="w-5 h-5 text-[#DA9C3A]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2L15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2z" />
                      </svg>
                    )}
                    {index === 3 && <Phone size={22} className="text-[#DA9C3A]" />}
                  </div>
                  <div>
                    <h3 className="font-bold text-[#622B62]">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
        <div className="w-8 h-12 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/80 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
